import { Quotes } from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';

/**
 * AboutBelowHero — Lives directly below the hero on all 3 home variants.
 * Mirrors the client reference screenshot: eyebrow, large serif headline,
 * body copy on the left, framed portrait on the right with a gold outline
 * and an offset quote-card behind it.
 */
export const AboutBelowHero = () => {
  return (
    <section
      data-testid="about-below-hero"
      className="bg-[#FBF8F1] py-24 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left — copy */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="flex items-center gap-3 text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase mb-6">
              <span className="h-px w-8 bg-[#D4AF37]" />
              About India
            </p>

            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A0000] leading-[1.05] mb-8">
              A Notary Who <span className="italic">Comes to You.</span>
            </h2>

            <div className="space-y-5 text-[#3C3C3C] leading-[1.85] text-[15px] sm:text-base max-w-2xl">
              <p>
                India Scruggs is a dedicated traveling notary public committed to making
                the notarization process as effortless as possible for individuals,
                families, and businesses throughout the Nashville metropolitan area.
                Licensed, bonded, and MBA-led, she serves Davidson, Williamson, Sumner,
                Rutherford, and surrounding counties with professional expertise and
                Nashville-native integrity.
              </p>
              <p>
                With an unwavering commitment to accuracy, professionalism, and client
                convenience, India brings the notary office to your doorstep — eliminating
                the need to take time off work or navigate downtown traffic.
              </p>
            </div>

            {/* Pull quote */}
            <div className="mt-10 pl-6 border-l-[3px] border-[#D4AF37] max-w-2xl">
              <Quotes weight="fill" size={24} className="text-[#D4AF37] mb-3" />
              <p className="font-serif-display italic text-xl sm:text-2xl text-[#800000] leading-relaxed">
                "Whether it's a life-changing document or routine paperwork, every
                signature deserves the same care and attention."
              </p>
              <p className="text-xs text-[#5C5C5C] mt-3 tracking-[0.2em] uppercase">
                — India Scruggs · Owner, MBA
              </p>
            </div>

            <p className="mt-10 text-[#3C3C3C] leading-[1.85] text-[15px] sm:text-base max-w-2xl">
              From loan signings and apostille filings to estate planning documents and
              wedding ceremonies, India handles each engagement with the precision and
              discretion your most important papers require.
            </p>
          </div>

          {/* Right — portrait */}
          <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="relative">
              {/* Offset gold outline frame */}
              <div className="absolute -inset-3 border-2 border-[#D4AF37] rounded-sm hidden sm:block" />
              <div className="absolute -inset-8 -bottom-12 border border-[#D4AF37]/30 rounded-sm hidden sm:block" />

              <div className="relative bg-white p-2 shadow-2xl">
                <img
                  src={BRAND.headshotUrl}
                  alt="India Scruggs — Owner, MBA · India's Inking Service, Nashville TN"
                  className="w-[280px] sm:w-[340px] aspect-[3/4] object-cover"
                />
              </div>

              {/* Floating credential card */}
              <div className="absolute -bottom-6 -left-6 bg-white px-5 py-3 shadow-xl border-l-4 border-[#D4AF37] hidden sm:block">
                <p className="font-serif-display text-base text-[#800000] italic leading-none">
                  India Scruggs
                </p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C5C] mt-1.5">
                  Owner · MBA · Nashville
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
